/**
 * Chat Widget Hook
 * Manages chat widget state, API integration, and WebSocket streaming
 * Following production-grade state management patterns
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../src/hooks/useAuth';
import type { ChatWidgetState, ChatState, ChatMessage, ChatConversation, ChatAuthState, ChatSendMessageRequest, RateLimit, ChatSuggestion, RateLimitType, StreamResumeData, FeedbackType, StreamPhase } from '../types/chat.types';
import type { ChatStreamChunk } from '../types/chat.types';
import {
  generateConversationId,
  generateMessageId,
  validateMessage,
  deleteConversation as deleteStoredConversation
} from '../utils/chatStorage';
import { useChatHistory } from './useChatHistory';
import { useChatWebSocket } from './useChatWebSocket';
import { chatApi } from '../../services/chatApi';
import { getApiConfig } from '../../../../lib/api/config';

/**
 * Threshold for auto-loading last conversation (7 days in milliseconds)
 */
const RECENT_CONVERSATION_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Categorize errors into specific types for better user messaging
 * Returns error type, technical message, and user-friendly message
 */
const categorizeError = (error: unknown): {
  type: 'cors' | 'network' | 'server' | 'auth' | 'rate_limit' | 'unknown';
  message: string;
  userMessage: string;
} => {
  if (error instanceof Error) {
    const errorMsg = error.message.toLowerCase();
    
    // CORS errors
    if (errorMsg.includes('cors') || errorMsg.includes('not allowed by cors') || 
        errorMsg.includes('access-control-allow-origin')) {
      return {
        type: 'cors',
        message: 'CORS policy violation',
        userMessage: 'Connection blocked. Please ensure you\'re accessing from amalq.org or www.amalq.org'
      };
    }
    
    // Network errors
    if (errorMsg.includes('network') || errorMsg.includes('failed to fetch') || 
        errorMsg.includes('timeout') || errorMsg.includes('networkerror')) {
      return {
        type: 'network',
        message: 'Network connection failed',
        userMessage: 'Network error. Please check your internet connection and try again.'
      };
    }
    
    // Server errors
    if (errorMsg.includes('500') || errorMsg.includes('server error') || 
        errorMsg.includes('internal server')) {
      return {
        type: 'server',
        message: 'Server error',
        userMessage: 'Server error. Please try again in a moment.'
      };
    }
    
    // Authentication errors
    if (errorMsg.includes('401') || errorMsg.includes('unauthorized') || 
        errorMsg.includes('authentication')) {
      return {
        type: 'auth',
        message: 'Authentication failed',
        userMessage: 'Session expired. Please log in again.'
      };
    }
    
    // Rate limit errors
    if (errorMsg.includes('rate limit') || errorMsg.includes('429') || 
        errorMsg.includes('too many requests')) {
      return {
        type: 'rate_limit',
        message: 'Rate limit exceeded',
        userMessage: 'Too many requests. Please wait a moment before trying again.'
      };
    }
  }
  
  return {
    type: 'unknown',
    message: 'Unknown error',
    userMessage: 'An error occurred. Please try again.'
  };
};

/**
 * Hook return type
 */
interface UseChatWidgetReturn {
  // State
  widgetState: ChatWidgetState;
  chatState: ChatState;
  messages: ChatMessage[];
  currentConversationId: string | null;
  hasUnreadMessages: boolean;
  authState: ChatAuthState;
  isStreaming: boolean;
  /** True only while waiting for first chunk; hide when streaming message is visible */
  showTypingIndicator: boolean;
  rateLimit: RateLimit | null;
  queuedMessagesCount: number;
  rateLimitRetryAfter: number | null;
  isLoadingConversation: boolean;
  loadingConversationId: string | null;
  showRateLimitModal: boolean;
  rateLimitError: string | null;
  rateLimitType: RateLimitType | null; // Which limit was hit: 'burst' or 'window'
  draftMessage: string; // FIX #3: Draft preservation

  // Actions
  openChat: () => Promise<void>;
  closeChat: () => void;
  toggleHistory: () => void;
  sendMessage: (content: string) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (conversationId: string) => void;
  continueAsGuest: () => void;
  closeRateLimitModal: () => void;
  handleSuggestionClick: (suggestion: ChatSuggestion) => Promise<void>;
  getSuggestionsForMessage: (messageId: string) => { suggestions: ChatSuggestion[]; isOffTopic: boolean } | null;
  retryMessage: (messageId: string) => Promise<void>; // FIX #5: Retry failed messages
  dismissSuggestions: (messageId: string) => void; // FIX #8: Dismiss suggestions
  setDraftMessage: (content: string) => void; // FIX #3: Update draft
  // Message actions
  submitFeedback: (messageId: string, feedbackType: FeedbackType, feedbackText?: string) => Promise<void>;
  regenerateMessage: (messageId: string) => Promise<void>;
  canRegenerateMessage: (messageId: string) => boolean; // Check if regeneration is possible
  // Feedback state (null = explicitly removed, undefined/not-in-map = check message.feedback)
  messageFeedback: Map<string, FeedbackType | null>;
}

/**
 * Hook for managing chat widget state with API and WebSocket integration
 */
export function useChatWidget(): UseChatWidgetReturn {
  const { authState: userAuthState } = useAuth();
  const navigate = useNavigate();
  const [widgetState, setWidgetState] = useState<ChatWidgetState>('closed');
  const [chatState, setChatState] = useState<ChatState>('idle');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [authState, setAuthState] = useState<ChatAuthState>('checking');
  const [streamPhase, setStreamPhase] = useState<StreamPhase>('idle');
  const [isGuest, setIsGuest] = useState(false);

  /** Derived: true when awaiting first chunk OR actively streaming */
  const isStreaming = streamPhase !== 'idle';
  /** Derived: true only while waiting for first chunk; hide when streaming message visible */
  const showTypingIndicator = streamPhase === 'awaiting_first_chunk';
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);

  const { saveConversation, conversations: historyConversations, isLoading: isLoadingHistory } = useChatHistory();
  const conversationRef = useRef<ChatConversation | null>(null);
  const streamingMessageRef = useRef<ChatMessage | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const messageQueueRef = useRef<string[]>([]); // Queue for rate-limited messages
  const queueProcessTimeoutRef = useRef<number | null>(null); // Timeout for processing queue
  const countdownIntervalRef = useRef<number | null>(null); // Interval for countdown timer
  const [queuedMessagesCount, setQueuedMessagesCount] = useState(0); // UI state for queue count
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null); // Countdown timer
  const [showRateLimitModal, setShowRateLimitModal] = useState(false); // Show rate limit modal
  const [rateLimitError, setRateLimitError] = useState<string | null>(null); // Rate limit error message
  const [rateLimitType, setRateLimitType] = useState<RateLimitType | null>(null); // Which limit was hit
  
  // Streaming state - paint buffer immediately, no word-reveal delay
  const streamingBufferRef = useRef<string>(''); // Buffer for incoming chunks
  const isCompleteRef = useRef<boolean>(false); // Flag to prevent race conditions
  const responseTimeoutRef = useRef<number | null>(null); // Timeout for stuck "responding" state
  const RESPONSE_TIMEOUT_MS = 60000; // 60 seconds timeout for response
  
  // Suggestions state management
  const suggestionsMapRef = useRef<Map<string, { suggestions: ChatSuggestion[]; isOffTopic: boolean }>>(new Map());
  const pendingSuggestionsRef = useRef<{ suggestions: ChatSuggestion[]; isOffTopic: boolean } | null>(null);

  // Track feedback state per message (persisted across renders)
  // null = explicitly removed, undefined (not in map) = not checked yet
  // Version state triggers re-renders when feedback changes (value intentionally unused)
  const messageFeedbackRef = useRef<Map<string, FeedbackType | null>>(new Map());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_feedbackVersion, setFeedbackVersion] = useState(0);
  
  // Track which conversation is currently loading (to prevent duplicate requests)
  const loadingConversationIdRef = useRef<string | null>(null);
  // Track conversations that failed to load with 404 (to prevent retry loops for deleted conversations)
  const failedConversationIdsRef = useRef<Set<string>>(new Set());
  const autoLoadAttemptedRef = useRef<boolean>(false); // Track if we've already attempted auto-load

  // FIX #3: Draft preservation - save draft message content for recovery
  const draftMessageRef = useRef<string>('');

  // FIX #6: Partial response preservation - save streaming buffer on tab close
  const partialResponseSavedRef = useRef<boolean>(false);

  // FIX #7: Chunk sequencing - track last chunk index for ordering safety
  const lastChunkIndexRef = useRef<number>(-1);

  // FIX #8: Dismissed suggestions - track which message suggestions have been dismissed
  const dismissedSuggestionsRef = useRef<Set<string>>(new Set());

  // NOTE: Stream resumption is handled by backend via StreamBufferService
  // Backend buffers chunks in Redis and sends 'stream_resume' event on reconnection

  /**
   * Get access token from localStorage
   */
  const getAccessToken = useCallback((): string | undefined => {
    try {
      const token = localStorage.getItem('amalq_user_access_token');
      accessTokenRef.current = token;
      return token || undefined;
    } catch {
      return undefined;
    }
  }, []);

  // Track previous auth state to detect logout
  const prevAuthStateRef = useRef<{ isAuthenticated: boolean; userId?: string }>({
    isAuthenticated: false
  });

  /**
   * Check authentication state
   * Also handles logout by clearing chat state
   */
  useEffect(() => {
    if (userAuthState.isLoading) {
      setAuthState('checking');
      return;
    }

    const wasAuthenticated = prevAuthStateRef.current.isAuthenticated;
    const isNowAuthenticated = userAuthState.isAuthenticated && !!userAuthState.user?.id;

    if (isNowAuthenticated) {
      setAuthState('authenticated');
      setIsGuest(false);
    } else {
      // User is not authenticated
      // Check if they just logged out (were authenticated, now not)
      if (wasAuthenticated && !isNowAuthenticated) {
        // User just logged out - clear all chat state
        setMessages([]);
        setCurrentConversationId(null);
        conversationRef.current = null;
        setChatState('idle');
        setIsGuest(false);
        // Close chat widget on logout for security
        setWidgetState('closed');
      }
      
      // Set auth state based on guest status
      if (isGuest) {
        setAuthState('guest');
      } else {
        setAuthState('prompt');
      }
    }

    // Update previous auth state
    prevAuthStateRef.current = {
      isAuthenticated: isNowAuthenticated,
      userId: userAuthState.user?.id
    };
  }, [userAuthState.isAuthenticated, userAuthState.user?.id, userAuthState.isLoading, isGuest]);

  // Ref to store sendMessage function for queue processing (avoids circular dependency)
  const sendMessageRef = useRef<((content: string) => Promise<void>) | null>(null);

  /**
   * Process queued messages after rate limit window expires
   */
  const processMessageQueue = useCallback(async (): Promise<void> => {
    if (messageQueueRef.current.length === 0) {
      setQueuedMessagesCount(0);
      return;
    }

    if (!sendMessageRef.current) {
      console.warn('sendMessage not available for queue processing');
      return;
    }

    // Process first message in queue
    const messageToSend = messageQueueRef.current.shift();
    if (messageToSend) {
      setQueuedMessagesCount(messageQueueRef.current.length);
      try {
        await sendMessageRef.current(messageToSend);
      } catch (error) {
        // If it fails again, re-queue it
        console.error('Failed to send queued message:', error);
        messageQueueRef.current.unshift(messageToSend);
        setQueuedMessagesCount(messageQueueRef.current.length);
      }
    }

    // Continue processing if more messages in queue
    if (messageQueueRef.current.length > 0 && rateLimit && rateLimit.remaining > 0) {
      // Process next message immediately if rate limit allows
      processMessageQueue();
    }
  }, [rateLimit]);

  /**
   * Queue a message for later processing when rate limited
   */
  const queueMessage = useCallback((message: string, retryAfter: number): void => {
    messageQueueRef.current.push(message);
    setQueuedMessagesCount(messageQueueRef.current.length);
    
    // Clear existing timeout
    if (queueProcessTimeoutRef.current) {
      window.clearTimeout(queueProcessTimeoutRef.current);
    }
    
    // Schedule queue processing after retryAfter seconds
    queueProcessTimeoutRef.current = window.setTimeout(() => {
      processMessageQueue();
    }, retryAfter * 1000);
  }, [processMessageQueue]);

  /**
   * Create a new conversation
   * NOTE: The generated ID is temporary - it will be replaced with the backend's ID
   * when the first message is sent via HTTP POST. The backend is the source of truth
   * for conversation IDs, and we always use the ID returned in the HTTP response.
   */
  const createNewConversation = useCallback((): ChatConversation => {
    // Generate temporary ID - will be replaced by backend's ID when HTTP POST returns
    const id = generateConversationId();
    const now = Date.now();
    
    const conversation: ChatConversation = {
      id,
      messages: [],
      startedAt: now,
      lastActivityAt: now,
      title: 'New Conversation',
      userId: userAuthState.user?.id,
      isGuest: !userAuthState.isAuthenticated || isGuest
    };
    
    conversationRef.current = conversation;
    setCurrentConversationId(id);
    return conversation;
  }, [userAuthState.user?.id, userAuthState.isAuthenticated, isGuest]);

  /**
   * Save current conversation to storage
   */
  const saveCurrentConversation = useCallback(() => {
    if (conversationRef.current) {
      const updated: ChatConversation = {
        ...conversationRef.current,
        messages,
        lastActivityAt: Date.now(),
        title: messages[0]?.content?.substring(0, 50) || 'New Conversation'
      };
      conversationRef.current = updated;
      saveConversation(updated);
    }
  }, [messages, saveConversation]);

  /**
   * Split text into words while preserving whitespace
   * Handles markdown formatting gracefully by preserving structure
   */
  /**
   * Handle Socket.IO stream chunks - paint buffer immediately, no artificial delay
   * FIX #7: Defensive chunk sequencing - logs out-of-order chunks (trust TCP ordering)
   */
  const handleStreamChunk = useCallback((chunk: ChatStreamChunk & { chunkIndex?: number }) => {
    try {
      // FIX #7: Defensive chunk sequencing check (logging only, trust TCP ordering)
      // Backend will add chunkIndex in future - this is defensive for when they do
      if (typeof chunk.chunkIndex === 'number') {
        const expectedIndex = lastChunkIndexRef.current + 1;
        if (chunk.chunkIndex !== expectedIndex && lastChunkIndexRef.current !== -1) {
          console.warn(
            `⚠️ Chunk sequence mismatch: expected ${expectedIndex}, got ${chunk.chunkIndex}`,
            { conversationId: chunk.conversationId, messageId: chunk.messageId }
          );
          // Don't reorder - trust TCP, just log for debugging
        }
        lastChunkIndexRef.current = chunk.chunkIndex;
      }

      // Reset completion flag and chunk index for new streaming session
      if (!streamingMessageRef.current) {
        isCompleteRef.current = false;
        lastChunkIndexRef.current = -1; // Reset chunk counter for new message
      }

      if (chunk.error) {
        streamingBufferRef.current = '';
        setStreamPhase('idle');
        setChatState('error');
        const errorMessage: ChatMessage = {
          id: generateMessageId(),
          type: 'system',
          content: chunk.error,
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
        streamingMessageRef.current = null;
        return;
      }

      // Accumulate chunks in buffer
      streamingBufferRef.current += chunk.content;

      // NOTE: Stream resumption is now handled by backend via StreamBufferService
      // Backend buffers chunks in Redis and sends stream_resume event on reconnection

      // Get or create streaming message
      const currentStreamingId = streamingMessageRef.current?.id;
      
      // CRITICAL: If chunk is complete, IMMEDIATELY use full buffer content
      // Don't rely on word-based calculations which can truncate content
      if (chunk.isComplete) {
        isCompleteRef.current = true;
        // Get final content
        const finalMessageId = currentStreamingId || chunk.messageId || generateMessageId();
        const finalContent = streamingBufferRef.current;
        
        // Single atomic update - no word reveal call
        if (!currentStreamingId) {
          const newMessage: ChatMessage = {
            id: finalMessageId,
            type: 'bot',
            content: finalContent,
            timestamp: Date.now(),
            isStreaming: false
          };
          setMessages(prev => [...prev, newMessage]);
        } else {
          setMessages(prev => {
            const index = prev.findIndex(m => m && m.id === finalMessageId);
            if (index >= 0) {
              const updated = [...prev];
              updated[index] = {
                ...updated[index],
                content: finalContent, // Full buffer
                isStreaming: false
              };
              return updated;
            }
            return prev;
          });
        }
        
        setStreamPhase('idle');
        setChatState('idle');
        
        // Associate suggestions with the completed bot message
        // WebSocket suggestions (from chunk) take precedence over HTTP suggestions
        if (finalMessageId) {
          // Priority 1: Suggestions from WebSocket chunk (most reliable, comes with complete chunk)
          if (chunk.suggestions && chunk.suggestions.length > 0) {
            suggestionsMapRef.current.set(finalMessageId, {
              suggestions: chunk.suggestions,
              isOffTopic: chunk.isOffTopic ?? false,
            });
            // Clear pending HTTP suggestions since WebSocket is the source of truth
            pendingSuggestionsRef.current = null;
          } 
          // Priority 2: Fallback to HTTP suggestions if WebSocket didn't provide them
          else if (pendingSuggestionsRef.current) {
            suggestionsMapRef.current.set(finalMessageId, {
              suggestions: pendingSuggestionsRef.current.suggestions,
              isOffTopic: pendingSuggestionsRef.current.isOffTopic,
            });
            // Clear pending suggestions after association
            pendingSuggestionsRef.current = null;
          }
        }
        
        streamingMessageRef.current = null;

        // NOTE: Stream resumption is now handled by backend - no frontend sessionStorage needed

        // Clear response timeout since response is complete
        if (responseTimeoutRef.current) {
          window.clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }

        // Delay clearing buffer
        setTimeout(() => {
          streamingBufferRef.current = '';
          isCompleteRef.current = false;
        }, 500);

        return;
      }

      // Incomplete chunks: paint buffer immediately (no word-reveal delay)
      const bufferContent = streamingBufferRef.current;
      if (!currentStreamingId) {
        // First chunk: create message, transition to streaming, clear timeout
        const messageId = chunk.messageId || generateMessageId();
        const newMessage: ChatMessage = {
          id: messageId,
          type: 'bot',
          content: bufferContent,
          timestamp: Date.now(),
          isStreaming: true
        };
        streamingMessageRef.current = newMessage;
        setMessages(prev => [...prev, newMessage]);

        setStreamPhase('streaming');
        if (responseTimeoutRef.current) {
          window.clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }
      } else {
        // Subsequent chunks: update message with full buffer
        setMessages(prev => {
          const index = prev.findIndex(m => m.id === currentStreamingId);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              content: bufferContent,
              isStreaming: true
            };
            return updated;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error handling stream chunk (non-fatal):', error);
      isCompleteRef.current = false;
      lastChunkIndexRef.current = -1;
      if (responseTimeoutRef.current) {
        window.clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      setStreamPhase('idle');
      setChatState('idle');
      streamingMessageRef.current = null;
      streamingBufferRef.current = '';
    }
  }, []);

  /**
   * Handle typing event from Socket.IO
   */
  const handleTyping = useCallback(() => {
    // Typing indicator is already shown via isStreaming state
    // This callback can be used for additional UI feedback if needed
    // No logging needed - UI handles the visual feedback
  }, []);

  /**
   * Handle complete event from Socket.IO
   * May include suggestions and isOffTopic flag for off-topic questions
   */
  const handleComplete = useCallback((messageId: string | null, metadata?: { isOffTopic?: boolean; suggestions?: ChatSuggestion[] }) => {
    try {
      // Set completion flag FIRST
      isCompleteRef.current = true;
      
      // Capture the current streaming message ID before state updates
      // This prevents null reference errors if ref is cleared during state update
      const currentStreamingId = streamingMessageRef.current?.id;
      
      if (!currentStreamingId) {
        // No streaming message to complete - just reset state
        setStreamPhase('idle');
        setChatState('idle');
        streamingMessageRef.current = null;
        isCompleteRef.current = false; // Reset flag
        return;
      }

      // Update message with final ID if provided
      if (messageId && currentStreamingId !== messageId) {
        setMessages(prev => {
          const index = prev.findIndex(m => m && m.id === currentStreamingId);
          if (index >= 0 && prev[index]) {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              id: messageId,
              isStreaming: false
            };
            return updated;
          }
          return prev;
        });
      } else {
        // Just mark as complete
        setMessages(prev => {
          const index = prev.findIndex(m => m && m.id === currentStreamingId);
          if (index >= 0 && prev[index]) {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              isStreaming: false
            };
            return updated;
          }
          return prev;
        });
      }
      
      // Clear response timeout since response is complete
      if (responseTimeoutRef.current) {
        window.clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      // Ensure all buffered content is shown
      const finalContent = streamingBufferRef.current;
      if (finalContent) {
        setMessages(prev => {
          const index = prev.findIndex(m => m && m.id === currentStreamingId);
          if (index >= 0 && prev[index]) {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              content: finalContent,
              isStreaming: false
            };
            return updated;
          }
          return prev;
        });
      }
      
      setStreamPhase('idle');
      setChatState('idle');
      
      // Associate suggestions with the completed bot message from WebSocket complete event
      // WebSocket suggestions take precedence over HTTP suggestions (WebSocket is source of truth)
      if (currentStreamingId) {
        // Priority 1: Suggestions from WebSocket complete event metadata (most reliable)
        if (metadata?.suggestions && metadata.suggestions.length > 0) {
          suggestionsMapRef.current.set(currentStreamingId, {
            suggestions: metadata.suggestions,
            isOffTopic: metadata.isOffTopic ?? false,
          });
          // Clear pending HTTP suggestions since WebSocket is the source of truth
          pendingSuggestionsRef.current = null;
        } 
        // Priority 2: Fallback to HTTP suggestions if WebSocket didn't provide them
        else if (pendingSuggestionsRef.current) {
          suggestionsMapRef.current.set(currentStreamingId, {
            suggestions: pendingSuggestionsRef.current.suggestions,
            isOffTopic: pendingSuggestionsRef.current.isOffTopic,
          });
          // Clear pending suggestions after association
          pendingSuggestionsRef.current = null;
        }
      }
      
      streamingMessageRef.current = null;
      streamingBufferRef.current = '';
      isCompleteRef.current = false;
    } catch (error) {
      console.error('Error handling complete event (non-fatal):', error);
      isCompleteRef.current = false;
      setStreamPhase('idle');
      setChatState('idle');
      streamingMessageRef.current = null;
      streamingBufferRef.current = '';
    }
  }, []);

  /**
   * Handle stream resume event from backend
   * Called when client reconnects during active/recent streaming
   * Backend sends all buffered content in one event
   */
  const handleStreamResume = useCallback((data: StreamResumeData) => {
    console.info('📥 STREAM RESUME: Received buffered content from backend', {
      conversationId: data.conversationId,
      chunkCount: data.chunkCount,
      isComplete: data.isComplete,
      isError: data.isError,
      contentLength: data.bufferedContent.length
    });

    // Ignore if conversation doesn't match
    if (currentConversationId && data.conversationId !== currentConversationId) {
      console.warn('Stream resume for wrong conversation, ignoring');
      return;
    }

    // Handle error case
    if (data.isError) {
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'system',
        content: data.errorMessage || 'The previous response encountered an error.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
      setStreamPhase('idle');
      setChatState('error');
      return;
    }

    // Create or update the message with buffered content
    const messageId = data.messageId || generateMessageId();
    const resumedMessage: ChatMessage = {
      id: messageId,
      type: 'bot',
      content: data.bufferedContent,
      timestamp: data.startedAt || Date.now(),
      isStreaming: !data.isComplete // Still streaming if not complete
    };

    // Check if message already exists (might have been added by previous recovery attempt)
    setMessages(prev => {
      const existingIndex = prev.findIndex(m => m.id === messageId);
      if (existingIndex >= 0) {
        // Update existing message
        const updated = [...prev];
        updated[existingIndex] = resumedMessage;
        return updated;
      }
      // Add new message
      return [...prev, resumedMessage];
    });

    if (data.isComplete) {
      // Stream is complete - mark as not streaming
      setStreamPhase('idle');
      setChatState('idle');
      streamingMessageRef.current = null;

      // Associate suggestions if available
      if (data.suggestions && data.suggestions.length > 0) {
        suggestionsMapRef.current.set(messageId, {
          suggestions: data.suggestions,
          isOffTopic: data.isOffTopic ?? false
        });
      }
    } else {
      // Stream is still active - we have buffered content, so we're in streaming phase
      setStreamPhase('streaming');
      setChatState('streaming');

      // Set up streaming state so future chunks can continue
      streamingBufferRef.current = data.bufferedContent;
      streamingMessageRef.current = resumedMessage;
    }
  }, [currentConversationId]);

  /**
   * Handle Socket.IO errors with structured error types
   */
  const handleSocketError = useCallback((error: string, errorType?: string) => {
    console.error('Socket.IO error:', error, errorType);

    // Clear response timeout on error
    if (responseTimeoutRef.current) {
      window.clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }

    // Handle conversation_not_found - clear stale conversation ID
    if (errorType === 'conversation_not_found' || errorType === 'CONVERSATION_NOT_FOUND') {
      // Clear stale conversation ID
      if (currentConversationId) {
        setCurrentConversationId(null);
      }
      if (conversationRef.current) {
        conversationRef.current = null;
      }
      // Reset streaming state
      setStreamPhase('idle');
      setChatState('idle');
      // Don't show error to user - this is expected when conversation doesn't exist
      // The handleConversationNotFound callback will also be called from useChatWebSocket
      return;
    }
    
    // Handle access_denied - retry logic will handle it
    if (errorType === 'access_denied') {
      // Log but don't show user error - retry logic will handle it
      console.warn('Access denied - retry logic will handle');
      // Still reset streaming state
      setStreamPhase('idle');
      setChatState('idle');
      return;
    }
    
    // Reset streaming state on connection errors
    if (errorType === 'connection_error' || error.includes('connection') || error.includes('WebSocket')) {
      setStreamPhase('idle');
      setChatState('error');
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'system',
        content: 'Connection error. Please check your internet connection and try again.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }
    
    setStreamPhase('idle');
    setChatState('error');
    const errorMessage: ChatMessage = {
      id: generateMessageId(),
      type: 'system',
      content: error,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, errorMessage]);
  }, []);

  const lastRefreshTimeRef = useRef<number>(0); // Track last refresh time to prevent excessive refreshes
  const MIN_REFRESH_INTERVAL_MS = 3000; // Minimum 3 seconds between refreshes

  /**
   * Refresh conversation state from API
   * Used when tab becomes visible again to ensure UI is up-to-date
   * SMART REFRESH: Only refreshes if enough time has passed since last refresh
   */
  const refreshConversationState = useCallback(async (): Promise<void> => {
    if (!currentConversationId) return;
    
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
    
    // Only refresh if it's been more than MIN_REFRESH_INTERVAL_MS since last refresh
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
      // Too soon since last refresh - skip this one
      return;
    }
    
    // Update last refresh time
    lastRefreshTimeRef.current = now;
    
    try {
      const token = getAccessToken();
      const response = await chatApi.getConversation(currentConversationId, token);
      
      if (response.data && response.data.messages) {
        // Update messages with latest from API
        // API response messages may have different structure, so we safely map them
        const apiMessages: ChatMessage[] = (response.data.messages as Array<{
          id: string;
          type: string;
          content: string;
          timestamp: string | number;
          role?: string;
          error?: string;
        }>).map((msg): ChatMessage => ({
          id: msg.id,
          type: (msg.type === 'user' || msg.type === 'bot' || msg.type === 'system' || msg.type === 'auth_prompt') 
            ? msg.type as ChatMessage['type']
            : 'system', // Default to system if type is unknown
          content: msg.content,
          timestamp: typeof msg.timestamp === 'string' ? new Date(msg.timestamp).getTime() : msg.timestamp
        }));
        
        setMessages(apiMessages);
        // Only log if actually refreshed (not skipped)
        console.log('✅ Chat: Refreshed conversation state from API');
      }
    } catch (error) {
      console.error('❌ Chat: Failed to refresh conversation state:', error);
      // Don't show error to user - silent refresh failure is acceptable
      // Reset last refresh time on error so we can retry sooner
      lastRefreshTimeRef.current = 0;
    }
  }, [currentConversationId, getAccessToken]);

  /**
   * Socket.IO connection for streaming responses
   * Connection is enabled when widget is open (regardless of conversationId)
   * Conversation joins happen explicitly after HTTP POST succeeds
   */
  /**
   * Handle conversation not found - clear stale conversation ID and auto-create new
   * Called when Socket.IO reports CONVERSATION_NOT_FOUND error
   * FIX #4: Instead of requiring manual action, auto-create new conversation
   */
  const handleConversationNotFound = useCallback((conversationId: string) => {
    // Clear stale conversation ID from state
    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
    }

    // Clear conversation ref if it matches
    if (conversationRef.current?.id === conversationId) {
      conversationRef.current = null;
    }

    // Remove from localStorage to prevent retry loops
    deleteStoredConversation(conversationId);

    // Add to failed set to prevent retrying
    failedConversationIdsRef.current.add(conversationId);

    // FIX #4: Auto-create new conversation instead of requiring manual action
    // This reduces friction when user clicks on stale history item
    const newConversationId = generateConversationId();
    const now = Date.now();

    const newConversation: ChatConversation = {
      id: newConversationId,
      messages: [],
      startedAt: now,
      lastActivityAt: now,
      title: 'New Conversation',
      isGuest: isGuest
    };

    // Set new conversation state
    conversationRef.current = newConversation;
    setCurrentConversationId(newConversationId);
    setMessages([]);
    setChatState('idle');

    // Show friendly message to user
    const systemMessage: ChatMessage = {
      id: generateMessageId(),
      type: 'system',
      content: 'Previous conversation expired. Starting a fresh conversation...',
      timestamp: now
    };
    setMessages([systemMessage]);

    // Note: Do NOT join Socket.IO room yet - will happen on first message send
  }, [currentConversationId, isGuest]);

  const {
    joinConversation,
    disconnect: disconnectSocket,
    isConnected: socketConnected,
    isConnecting: socketConnecting,
    reconnect: socketReconnect,
    clearStalePendingOperations,
    updatePendingOperationsConversationId
  } = useChatWebSocket({
    onChunk: handleStreamChunk,
    onTyping: handleTyping,
    onComplete: handleComplete,
    onError: handleSocketError,
    onStreamResume: handleStreamResume, // Handle buffered content on reconnection
    onOpen: () => {
      // Socket connection established - no logging needed for normal operation
      // NOTE: Do NOT auto-join conversations here - must wait for HTTP POST to create/verify them
    },
    onClose: () => {
      // Socket connection closed - no logging needed for normal operation
    },
    enabled: widgetState === 'open', // Enable when widget is open, regardless of conversationId
    accessToken: getAccessToken(),
    currentConversationId: currentConversationId, // Pass for rejoin on visibility
    onRejoinNeeded: refreshConversationState, // Refresh state when tab becomes visible
    onConversationNotFound: handleConversationNotFound // Clear stale IDs when conversation doesn't exist
  });

  // Track socket connection state in a ref to avoid closure issues in async functions
  const socketConnectedRef = useRef<boolean>(socketConnected);
  useEffect(() => {
    socketConnectedRef.current = socketConnected;
  }, [socketConnected]);

  /**
   * Load a specific conversation
   * Phase 1: Always load history via HTTP API first (even if in localStorage)
   * Phase 2: Join Socket.IO room for real-time updates
   * Must be defined before openChat which uses it
   */
  const loadConversation = useCallback(async (conversationId: string): Promise<void> => {
    // Prevent loading if this conversation already failed with 404 (deleted)
    if (failedConversationIdsRef.current.has(conversationId)) {
      console.warn(`Conversation ${conversationId} previously failed to load - skipping`);
      return;
    }

    // Prevent duplicate requests for the same conversation
    if (loadingConversationIdRef.current === conversationId) {
      console.warn(`Conversation ${conversationId} is already loading - skipping duplicate request`);
      return;
    }

    // Mark as currently loading (both ref for duplicate prevention and state for UI)
    loadingConversationIdRef.current = conversationId;
    setLoadingConversationId(conversationId);
    setIsLoadingConversation(true);
    
    try {
      // Phase 1: Always load from API first to ensure latest data
      // Token is optional - API works for both authenticated and guest users
      const token = getAccessToken();
      
      const response = await chatApi.getConversation(conversationId, token);
      
      if (response.data) {
        // Verify conversation ID matches (API response confirms conversation exists)
        const verifiedConversationId = response.data.id || conversationId;

        // Set messages from API (ensures latest data, chronological order)
        // Backend returns messages in chronological order (oldest first)
        setMessages(response.data.messages);
        conversationRef.current = response.data;

        // Initialize feedback state from messages (feedback is embedded in bot messages)
        // This replaces the need for a separate API call to fetch feedback
        messageFeedbackRef.current.clear();
        for (const msg of response.data.messages) {
          if (msg.type === 'bot' && msg.feedback?.feedbackType) {
            messageFeedbackRef.current.set(msg.id, msg.feedback.feedbackType);
          }
        }
        setFeedbackVersion(v => v + 1);
        setCurrentConversationId(verifiedConversationId);
        setChatState('idle');
        setWidgetState('open');
        
        // Save to localStorage for offline access
        saveConversation(response.data);
        
        // Phase 2: Join Socket.IO room for real-time updates
        // CRITICAL: Only join conversations that we know exist (from API response)
        // The verifiedConversationId from API response confirms the conversation exists
        // This allows receiving new streaming messages after history is loaded
        // For guests: pass undefined so backend generates guestId from socket connection
        // For authenticated: pass undefined (backend uses userId from token)
        try {
          // Verify conversation ID matches before joining
          if (verifiedConversationId && response.data.id === verifiedConversationId) {
            joinConversation(verifiedConversationId, undefined);
          } else {
            // Conversation ID mismatch - don't join stale ID
            console.warn('Conversation ID mismatch from API response - not joining');
            setCurrentConversationId(null);
            conversationRef.current = null;
          }
        } catch (joinError) {
          // Log but don't fail - widget should stay open even if join fails
          // If conversation doesn't exist, handleConversationNotFound will clear stale ID
          console.error('Failed to join conversation room (non-fatal):', joinError);
        }
      }
    } catch (error) {
      // Check if error is due to conversation not found
      const isNotFoundError = error && typeof error === 'object' && 'message' in error && (
        String(error.message).includes('not found') || 
        String(error.message).includes('404') ||
        (error as any)?.status === 404 ||
        (error as any)?.response?.status === 404
      );
      
      // If conversation not found, clear stale ID immediately
      if (isNotFoundError) {
        // Mark as failed so we don't retry loading a deleted conversation
        failedConversationIdsRef.current.add(conversationId);
        loadingConversationIdRef.current = null;
        // CRITICAL: Remove stale conversation from localStorage to prevent future load attempts
        try {
          deleteStoredConversation(conversationId);
        } catch (storageError) {
          console.warn('Failed to delete stale conversation from storage:', storageError);
        }
        setCurrentConversationId(null);
        conversationRef.current = null;
        setMessages([]);
        setChatState('idle');
        setIsLoadingConversation(false);
        return;
      }
      
      // Check if user is not authenticated - show auth gate instead of error
      if (authState === 'prompt' || authState === 'checking') {
        // User is not authenticated - clear messages and show auth gate
        loadingConversationIdRef.current = null;
        setMessages([]);
        setCurrentConversationId(null);
        conversationRef.current = null;
        setChatState('idle');
        setIsLoadingConversation(false);
        return;
      }
      
      // Handle errors with user-friendly messages for authenticated users
      let errorMessage = 'Failed to load conversation. Please try again.';
      
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMsg = String(error.message);
        
        if (errorMsg.includes('not found') || errorMsg.includes('404')) {
          errorMessage = 'Conversation not found.';
          // CRITICAL: Remove stale conversation from localStorage on 404
          try {
            deleteStoredConversation(conversationId);
          } catch (storageError) {
            console.warn('Failed to delete stale conversation from storage:', storageError);
          }
        } else if (errorMsg.includes('access') || errorMsg.includes('403')) {
          // 403 Forbidden - user doesn't have access
          loadingConversationIdRef.current = null;

          // CRITICAL: Remove stale conversation from localStorage
          try {
            deleteStoredConversation(conversationId);
          } catch (storageError) {
            console.warn('Failed to delete stale conversation from storage:', storageError);
          }

          // If user is authenticated, show error. Otherwise, show auth gate.
          if (authState === 'authenticated') {
            errorMessage = 'You do not have access to this conversation.';
          } else if (authState === 'guest') {
            // Guest user got 403 - might be trying to access wrong conversation
            setMessages([]);
            setCurrentConversationId(null);
            conversationRef.current = null;
            setChatState('idle');
            setIsLoadingConversation(false);
            return;
          } else {
            // Not authenticated and not guest - show auth gate
            setMessages([]);
            setCurrentConversationId(null);
            conversationRef.current = null;
            setChatState('idle');
            setAuthState('prompt');
            setIsGuest(false);
            setIsLoadingConversation(false);
            return;
          }
        } else if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
          // If we get 401, user is not authenticated (session expired or logged out)
          loadingConversationIdRef.current = null;
          setMessages([]);
          setCurrentConversationId(null);
          conversationRef.current = null;
          setChatState('idle');
          setAuthState('prompt');
          setIsGuest(false);
          setIsLoadingConversation(false);
          return;
        } else {
          // Use error categorization for better error messages
          const categorized = categorizeError(error);
          errorMessage = categorized.userMessage;
          // Log error type for debugging
          console.error(`Chat API Error [${categorized.type}]:`, error);
        }
      }
      
      // Display error to user via system message (only for authenticated users)
      const errorSystemMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'system',
        content: errorMessage,
        timestamp: Date.now()
      };
      setMessages([errorSystemMessage]);
      setChatState('error');
      // Keep widget open even on error - don't close it
      // Widget state should remain 'open' so user can see the error and try again
    } finally {
      // Always clear loading state (both ref and state)
      loadingConversationIdRef.current = null;
      setLoadingConversationId(null);
      setIsLoadingConversation(false);
    }
  }, [getAccessToken, saveConversation, joinConversation, authState]);

  /**
   * Get most recent conversation from history
   * Returns conversation if it's within the 7-day threshold, null otherwise
   */
  const getMostRecentConversation = useCallback((): ChatConversation | null => {
    if (!historyConversations || historyConversations.length === 0) {
      return null;
    }

    // Conversations are already sorted by lastActivityAt (newest first)
    const mostRecent = historyConversations[0];
    const now = Date.now();
    const timeSinceLastActivity = now - mostRecent.lastActivityAt;

    // Check if conversation is within 7-day threshold
    if (timeSinceLastActivity <= RECENT_CONVERSATION_THRESHOLD_MS) {
      return mostRecent;
    }

    return null;
  }, [historyConversations]);

  /**
   * Get most recent guest conversation (for auto-resume)
   * Returns the most recent guest conversation if available
   */
  const getMostRecentGuestConversation = useCallback((): ChatConversation | null => {
    if (!historyConversations || historyConversations.length === 0) {
      return null;
    }

    // Find most recent guest conversation
    const guestConversations = historyConversations.filter(conv => conv.isGuest === true);
    if (guestConversations.length === 0) {
      return null;
    }

    // Already sorted by lastActivityAt (newest first)
    const mostRecent = guestConversations[0];
    const now = Date.now();
    const timeSinceLastActivity = now - mostRecent.lastActivityAt;

    // Check if conversation is within 7-day threshold
    if (timeSinceLastActivity <= RECENT_CONVERSATION_THRESHOLD_MS) {
      return mostRecent;
    }

    return null;
  }, [historyConversations]);

  /**
   * Open chat widget
   * Auto-loads last conversation if within 7 days, otherwise shows empty state
   * For unauthenticated users, shows auth gate instead
   * GUEST AUTO-RESUME: Automatically resumes last guest conversation if available
   */
  const openChat = useCallback(async () => {
    setWidgetState('open');
    setHasUnreadMessages(false);
    
    // Reset auto-load flag when opening chat (allows retry if needed)
    autoLoadAttemptedRef.current = false;
    
    // If there's already an active conversation, keep it
    if (currentConversationId) {
      return;
    }

    // If user is not authenticated and not a guest, show auth gate
    // Don't try to load conversations or create new ones
    if (authState === 'prompt') {
      setMessages([]);
      setCurrentConversationId(null);
      conversationRef.current = null;
      setChatState('idle');
      return;
    }

    // GUEST USERS: Auto-resume last guest conversation if available
    if (authState === 'guest' || isGuest) {
      const recentGuestConv = getMostRecentGuestConversation();
      if (recentGuestConv) {
        try {
          await loadConversation(recentGuestConv.id);
          return; // Successfully loaded guest conversation
        } catch (error) {
          // If loading fails, create new guest conversation
          console.warn('Failed to load guest conversation, creating new one:', error);
          setIsGuest(true);
          setAuthState('guest');
          createNewConversation();
          return;
        }
      } else {
        // No recent guest conversation - create new one
        setIsGuest(true);
        setAuthState('guest');
        createNewConversation();
        return;
      }
    }

    // AUTHENTICATED USERS: Check for most recent conversation
    const recentConv = getMostRecentConversation();
    
    if (recentConv) {
      // Auto-load the recent conversation
      try {
        await loadConversation(recentConv.id);
      } catch (error) {
        // If loading fails, create new conversation
        // Failed to load recent conversation - create new one instead
        createNewConversation();
      }
    } else {
      // No recent conversation or it's older than 7 days
      // Show empty state (createNewConversation sets up empty state)
      createNewConversation();
    }
  }, [currentConversationId, createNewConversation, getMostRecentConversation, getMostRecentGuestConversation, loadConversation, authState, isGuest]);

  /**
   * Close chat widget
   */
  const closeChat = useCallback(() => {
    setWidgetState('closed');
    disconnectSocket();
    saveCurrentConversation();
  }, [saveCurrentConversation, disconnectSocket]);

  /**
   * Toggle history panel
   */
  const toggleHistory = useCallback(() => {
    setWidgetState(prev => prev === 'history' ? 'open' : 'history');
  }, []);

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(() => {
    saveCurrentConversation();
    disconnectSocket();
    // Clear failed conversations when starting new (allow retry)
    failedConversationIdsRef.current.clear();
    loadingConversationIdRef.current = null;
    autoLoadAttemptedRef.current = false;
    createNewConversation();
    setMessages([]);
    setChatState('idle');
    setWidgetState('open');
  }, [saveCurrentConversation, createNewConversation, disconnectSocket]);

  /**
   * Continue as guest
   * Sets guest mode and creates a new conversation
   */
  const continueAsGuest = useCallback(() => {
    setIsGuest(true);
    setAuthState('guest');
    // Clear any existing messages/conversations
    setMessages([]);
    setCurrentConversationId(null);
    conversationRef.current = null;
    setChatState('idle');
    // Create new guest conversation
    createNewConversation();
    setWidgetState('open');
  }, [createNewConversation]);

  /**
   * Wait for socket connection with timeout
   * Returns true if connected, false if timeout reached
   * Uses ref to avoid closure issues with stale state
   */
  const waitForSocketConnection = useCallback(async (timeoutMs: number = 5000): Promise<boolean> => {
    // Already connected - check both state and ref for immediate check
    if (socketConnected || socketConnectedRef.current) return true;

    // Trigger reconnect if not connecting
    if (!socketConnecting) {
      socketReconnect();
    }

    // Wait for connection with polling
    const startTime = Date.now();
    const checkInterval = 100; // Check every 100ms

    return new Promise((resolve) => {
      const checkConnection = (): void => {
        // Check ref for current connection state (avoids closure issues)
        if (socketConnectedRef.current) {
          resolve(true);
          return;
        }

        // Check if timeout reached
        if (Date.now() - startTime >= timeoutMs) {
          resolve(false);
          return;
        }

        // Keep checking
        setTimeout(checkConnection, checkInterval);
      };

      checkConnection();
    });
  }, [socketConnected, socketConnecting, socketReconnect]);

  /**
   * Send a message
   * IMPROVED: Waits for socket reconnection instead of failing immediately
   */
  const sendMessage = useCallback(async (content: string): Promise<void> => {
    // Validate message
    const validation = validateMessage(content);
    if (!validation.valid) {
      setChatState('error');
      throw new Error(validation.error);
    }

    // IMPROVED: Wait for socket connection instead of failing immediately
    // This handles temporary disconnections gracefully
    // Use ref for real-time check to avoid stale state
    if (!socketConnected && !socketConnectedRef.current) {
      setChatState('sending'); // Show loading state while waiting for connection

      const connected = await waitForSocketConnection(5000); // 5 second timeout

      if (!connected) {
        // Still not connected after waiting - show error
        const errorMessage: ChatMessage = {
          id: generateMessageId(),
          type: 'system',
          content: 'Unable to connect to chat service. Please check your internet connection and try again.',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
        setChatState('error');
        throw new Error('Socket connection timeout');
      }
      // Connected! Continue with message sending
    }

    // Store user message ID for potential rollback
    let userMessageId: string | null = null;
    let optimisticMessageAdded = false;

    try {
      setChatState('sending');

      // FIX #3: Save draft message for recovery
      draftMessageRef.current = content.trim();

      // Create user message with status tracking (FIX #5)
      const userMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'user',
        content: content.trim(),
        timestamp: Date.now(),
        status: 'sending' // Track send state for retry functionality
      };
      userMessageId = userMessage.id;

      // Add user message immediately (optimistic update)
      setMessages(prev => {
        optimisticMessageAdded = true;
        return [...prev, userMessage];
      });

      // Ensure we have a conversation
      if (!conversationRef.current) {
        createNewConversation();
      }

      const conversationId = conversationRef.current?.id || currentConversationId;
      const token = getAccessToken();

      // Prepare context - only include referrer if it's not empty
      const context = {
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        // Only include referrer if it exists and is not empty
        ...(document.referrer && document.referrer.trim() ? { referrer: document.referrer.trim() } : {})
      };

      // Send message via HTTP API to initiate conversation/streaming
      // Do NOT send isGuest when user is authenticated - backend determines this from userId
      try {
        const requestBody: ChatSendMessageRequest = {
          conversationId: conversationId || undefined,
          message: content.trim(),
          context: Object.keys(context).length > 0 ? context : undefined,
        };
        
        // Only include isGuest if user is NOT authenticated
        // Backend will reject if authenticated user sends isGuest: true
        if (!userAuthState.isAuthenticated) {
          requestBody.isGuest = isGuest;
        }
        
        const apiResponse = await chatApi.sendMessage(requestBody, token);

        // Update rate limit from response headers (sent on every response)
        if (apiResponse.rateLimit) {
          setRateLimit(apiResponse.rateLimit);
        }

        // Extract suggestions and isOffTopic from API response
        // Note: chatApi.sendMessage() now unwraps the response, so apiResponse.data is the actual data object
        if (apiResponse.data?.suggestions && apiResponse.data.suggestions.length > 0) {
          pendingSuggestionsRef.current = {
            suggestions: apiResponse.data.suggestions,
            isOffTopic: apiResponse.data.isOffTopic ?? false,
          };
        } else {
          // Clear pending suggestions if none provided
          pendingSuggestionsRef.current = null;
        }

        // CRITICAL: Always use conversationId from HTTP response as the source of truth
        // Backend may create a new conversation if the provided ID doesn't exist
        // We MUST use the ID returned by the backend, not the one we generated
        // Note: chatApi.sendMessage() unwraps the response, so apiResponse.data.conversationId is correct
        const responseConversationId = apiResponse.data?.conversationId;
        
        if (!responseConversationId) {
          // API didn't return conversationId - this shouldn't happen, but don't proceed
          console.error('HTTP POST succeeded but no conversationId in response - cannot join Socket.IO room');
          setStreamPhase('awaiting_first_chunk'); // Still show typing indicator, but can't receive streaming
          return;
        }
        
        // CRITICAL: If conversationId changed, clear stale operations from pending queue
        // This prevents joining with old conversationId that was queued before HTTP POST completed
        if (conversationId && conversationId !== responseConversationId) {
          // Conversation ID changed - clear stale operations for old ID
          clearStalePendingOperations(conversationId);
          // Update any remaining operations with new ID
          updatePendingOperationsConversationId(conversationId, responseConversationId);
        }
        
        // Update conversation ID immediately with the one from HTTP response
        // This ensures state and ref are synchronized with backend's actual conversation ID
        if (responseConversationId !== currentConversationId) {
          setCurrentConversationId(responseConversationId);
        }
        
        // Update conversationRef with the verified ID from backend
        if (conversationRef.current) {
          conversationRef.current.id = responseConversationId;
        } else {
          // If conversationRef is null, create it with the verified ID
          const verifiedConversation: ChatConversation = {
            id: responseConversationId,
            messages,
            startedAt: Date.now(),
            lastActivityAt: Date.now(),
            title: 'New Conversation',
            userId: userAuthState.user?.id,
            isGuest: !userAuthState.isAuthenticated || isGuest
          };
          conversationRef.current = verifiedConversation;
        }

        // ALWAYS join conversation room AFTER HTTP POST completes with verified conversationId
        // CRITICAL: Only join conversations that we know exist (from HTTP response)
        // The responseConversationId is the only source of truth - backend confirmed this conversation exists
        // For guests: pass undefined so backend generates guestId from socket connection
        // For authenticated: pass undefined (backend uses userId from token)
        // Wrap in try-catch to prevent errors from closing the widget
        try {
          // Join with the verified conversationId from HTTP response
          // This ensures Socket.IO join uses the same ID that backend created/verified
          joinConversation(responseConversationId, undefined);
        } catch (joinError) {
          // Log error but don't throw - widget should stay open even if join fails
          // If conversation doesn't exist, handleConversationNotFound will clear stale ID
          console.error('Failed to join conversation room (non-fatal):', joinError);
        }

        // HTTP POST only returns user message confirmation
        // Bot responses come via Socket.IO streaming chunks
        // Show typing indicator immediately while waiting for AI response
        setStreamPhase('awaiting_first_chunk');
        setChatState('streaming');

        // Set timeout to reset "responding" state if no response comes
        // This prevents the UI from being stuck in "responding" state forever
        if (responseTimeoutRef.current) {
          window.clearTimeout(responseTimeoutRef.current);
        }
        responseTimeoutRef.current = window.setTimeout(() => {
          // If still streaming after timeout, reset state and show error
          // Use ref to check current state to avoid stale closure
          setChatState(currentState => {
            if (currentState === 'streaming') {
              console.warn('⚠️ Chat: Response timeout - no response received within', RESPONSE_TIMEOUT_MS, 'ms');
              setStreamPhase('idle');
              const timeoutMessage: ChatMessage = {
                id: generateMessageId(),
                type: 'system',
                content: 'Sorry, the response is taking longer than expected. Please try again or check your connection.',
                timestamp: Date.now()
              };
              setMessages(prev => [...prev, timeoutMessage]);
              responseTimeoutRef.current = null;
              return 'error';
            }
            return currentState;
          });
        }, RESPONSE_TIMEOUT_MS);

        // Socket.IO will handle streaming chunks via handleStreamChunk
        // The typing indicator will be replaced by the first chunk

        // Save conversation with new message only after successful API call
        if (conversationRef.current) {
          const updated: ChatConversation = {
            ...conversationRef.current,
            messages: [...messages, userMessage],
            lastActivityAt: Date.now()
          };
          conversationRef.current = updated;
          saveConversation(updated);
        }

      } catch (apiError) {
        console.error('Failed to send message via API:', apiError);
        
        // Handle rate limit errors (429) - update rate limit state, show modal, and queue message
        if (apiError && typeof apiError === 'object' && 'rateLimit' in apiError) {
          const rateLimitErrorData = apiError as {
            rateLimit?: RateLimit;
            retryAfter?: number;
            message?: string;
            limitType?: RateLimitType; // Which limit was hit: 'burst' or 'window'
          };

          if (rateLimitErrorData.rateLimit) {
            setRateLimit(rateLimitErrorData.rateLimit);

            // Capture which limit was hit (burst vs window)
            // This allows UI to show contextually appropriate messages
            const limitType = rateLimitErrorData.limitType || 'burst'; // Default to burst
            setRateLimitType(limitType);

            // Extract error message from API (use exact message from backend)
            const errorMessage = rateLimitErrorData.message ||
              (apiError && typeof apiError === 'object' && 'message' in apiError
                ? String(apiError.message)
                : limitType === 'burst'
                  ? 'Slow down! Too many messages too quickly.'
                  : 'Rate limit reached. Please try again later.');

            setRateLimitError(errorMessage);
            setShowRateLimitModal(true);
            
            // Queue the message for later processing
            const retryAfterSeconds = rateLimitErrorData.retryAfter || 60;
            queueMessage(content.trim(), retryAfterSeconds);
            setRateLimitRetryAfter(retryAfterSeconds);
            
            // Clear existing countdown interval
            if (countdownIntervalRef.current) {
              window.clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            
            // Start countdown timer
            let remaining = retryAfterSeconds;
            countdownIntervalRef.current = window.setInterval(() => {
              remaining -= 1;
              setRateLimitRetryAfter(remaining);
              
              if (remaining <= 0) {
                if (countdownIntervalRef.current) {
                  window.clearInterval(countdownIntervalRef.current);
                  countdownIntervalRef.current = null;
                }
                setRateLimitRetryAfter(null);
                // Auto-close modal when countdown reaches zero
                setShowRateLimitModal(false);
                setRateLimitError(null);
              }
            }, 1000);
            
            // Don't show error message in chat - modal handles it
            // Rollback optimistic update since message is queued
            if (optimisticMessageAdded && userMessageId) {
              setMessages(prev => prev.filter(msg => msg.id !== userMessageId));
            }
            
            setChatState('idle');
            setStreamPhase('idle');
            return; // Exit early - message is queued
          }
        }
        
        // FIX #5: Keep failed message with status instead of removing it
        // This allows users to retry without re-typing
        if (optimisticMessageAdded && userMessageId) {
          setMessages(prev => prev.map(msg =>
            msg.id === userMessageId
              ? { ...msg, status: 'failed' as const, error: 'Failed to send message' }
              : msg
          ));
        }

        // Extract user-friendly error message
        let errorMessageText = 'Sorry, I encountered an error. Please try again.';
        
        if (apiError && typeof apiError === 'object' && 'message' in apiError) {
          const errorMsg = String(apiError.message);
          
          // Check for validation errors
          if (apiError && typeof apiError === 'object' && 'fields' in apiError) {
            // Parse validation field errors into user-friendly message
            const fieldErrors = (apiError as { fields?: Record<string, string[]> }).fields;
            if (fieldErrors) {
              const errorMessages = Object.values(fieldErrors).flat();
              if (errorMessages.length > 0) {
                errorMessageText = errorMessages[0]; // Show first validation error
              }
            }
          } else if (errorMsg) {
            // Use the error message directly if available
            errorMessageText = errorMsg;
          }
        }
        
        // Show error message to user (unless it's a rate limit error - those are handled differently)
        if (!(apiError && typeof apiError === 'object' && 'rateLimit' in apiError)) {
          const errorMessage: ChatMessage = {
            id: generateMessageId(),
            type: 'system',
            content: errorMessageText,
            timestamp: Date.now()
          };
          
          setMessages(prev => [...prev, errorMessage]);
          setChatState('error');
        }
        
        setStreamPhase('idle');
        
        throw apiError;
      }

    } catch (error) {
      // Final error handling - ensure state is reset
      setChatState('error');
      setStreamPhase('idle');

      // FIX #5: Keep failed message with status instead of removing it
      // This allows users to retry without re-typing
      if (optimisticMessageAdded && userMessageId) {
        const errorText = error instanceof Error ? error.message : 'Failed to send message';
        setMessages(prev => prev.map(msg =>
          msg.id === userMessageId
            ? { ...msg, status: 'failed' as const, error: errorText }
            : msg
        ));
      }

      console.error('Failed to send message:', error);
      throw error;
    }
  }, [
    messages,
    currentConversationId,
    createNewConversation,
    saveConversation,
    getAccessToken,
    userAuthState.isAuthenticated,
    isGuest,
    joinConversation,
    queueMessage,
    socketConnected,
    socketConnecting
  ]);

  // Update sendMessage ref whenever sendMessage changes
  useEffect((): void => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Auto-process queue when rate limit resets
  useEffect(() => {
    if (rateLimit && rateLimit.remaining > 0 && messageQueueRef.current.length > 0) {
      // Rate limit has reset - process queue
      processMessageQueue();
      // Close modal if open
      if (showRateLimitModal) {
        setShowRateLimitModal(false);
        setRateLimitError(null);
      }
    }
  }, [rateLimit, processMessageQueue, showRateLimitModal]);

  /**
   * Close rate limit modal
   */
  const closeRateLimitModal = useCallback(() => {
    setShowRateLimitModal(false);
    setRateLimitError(null);
  }, []);

  /**
   * Handle suggestion click
   * Sends suggestion text as a new message and optionally navigates to actionUrl
   * Uses React Router for internal navigation, falls back to window.location for external URLs
   */
  const handleSuggestionClick = useCallback(async (suggestion: ChatSuggestion) => {
    try {
      // Primary action: Send suggestion text as message
      await sendMessage(suggestion.text);
      
      // Secondary action: Navigate if URL provided (after sending)
      if (suggestion.actionUrl) {
        const actionUrl = suggestion.actionUrl; // Store in const for type safety
        // Small delay to ensure message is sent and UI updates
        setTimeout(() => {
          try {
            // Check if URL is internal (same origin) or external
            const url = new URL(actionUrl, window.location.origin);
            const isInternal = url.origin === window.location.origin;
            
            if (isInternal) {
              // Use React Router for internal navigation (preserves SPA state)
              navigate(url.pathname + url.search + url.hash);
            } else {
              // Use window.location for external URLs
              window.location.href = actionUrl;
            }
          } catch (urlError) {
            // If URL parsing fails, treat as external
            console.warn('Failed to parse actionUrl, treating as external:', urlError);
            window.location.href = actionUrl;
          }
        }, 500);
      }
    } catch (error) {
      console.error('Failed to handle suggestion click:', error);
      // If sending fails, still navigate if URL provided (user might want to go there anyway)
      if (suggestion.actionUrl) {
        const actionUrl = suggestion.actionUrl; // Store in const for type safety
        try {
          const url = new URL(actionUrl, window.location.origin);
          const isInternal = url.origin === window.location.origin;
          
          if (isInternal) {
            navigate(url.pathname + url.search + url.hash);
          } else {
            window.location.href = actionUrl;
          }
        } catch (urlError) {
          window.location.href = actionUrl;
        }
      }
    }
  }, [sendMessage, navigate]);

  /**
   * Get suggestions for a specific message
   * Returns suggestions and isOffTopic flag if available
   * FIX #8: Filters out dismissed suggestions
   */
  const getSuggestionsForMessage = useCallback((messageId: string): { suggestions: ChatSuggestion[]; isOffTopic: boolean } | null => {
    // FIX #8: Check if suggestions were dismissed for this message
    if (dismissedSuggestionsRef.current.has(messageId)) {
      return null;
    }
    return suggestionsMapRef.current.get(messageId) || null;
  }, []);

  /**
   * FIX #5: Retry a failed message
   * Finds the failed message, updates its status to 'retrying', and resends it
   */
  const retryMessage = useCallback(async (messageId: string): Promise<void> => {
    const failedMessage = messages.find(msg => msg.id === messageId);
    if (!failedMessage || failedMessage.status !== 'failed') {
      console.warn('Cannot retry message - not found or not failed:', messageId);
      return;
    }

    // Update message status to 'retrying'
    setMessages(prev => prev.map(msg =>
      msg.id === messageId
        ? { ...msg, status: 'retrying' as const, error: undefined }
        : msg
    ));

    try {
      // Remove the failed message before retrying (sendMessage will add it back)
      setMessages(prev => prev.filter(msg => msg.id !== messageId));

      // Resend the message content
      await sendMessage(failedMessage.content);
    } catch (error) {
      console.error('Retry failed:', error);
      // Message will be marked as failed by sendMessage error handling
    }
  }, [messages, sendMessage]);

  /**
   * FIX #8: Dismiss suggestions for a specific message
   * Adds the messageId to the dismissed set so suggestions won't be shown
   */
  const dismissSuggestions = useCallback((messageId: string): void => {
    dismissedSuggestionsRef.current.add(messageId);
    // Force a re-render by updating a state (messages won't change, but suggestions will hide)
    // Using a slight trick - we trigger a state update that won't change the UI
    // but will cause components that depend on getSuggestionsForMessage to re-evaluate
    setMessages(prev => [...prev]);
  }, []);

  /**
   * FIX #3: Set draft message content
   * Used to preserve typed content across token refreshes or recoverable errors
   */
  const setDraftMessage = useCallback((content: string): void => {
    draftMessageRef.current = content;
  }, []);

  /**
   * Submit feedback for a message (thumbs up/down)
   * Backend handles toggle logic:
   * - Click same button twice = remove feedback (undo)
   * - Click different button = switch feedback
   * Response actions: "created" | "updated" | "removed"
   */
  const submitFeedback = useCallback(async (
    messageId: string,
    feedbackType: FeedbackType,
    feedbackText?: string
  ): Promise<void> => {
    if (!currentConversationId) {
      console.warn('Cannot submit feedback - no active conversation');
      return;
    }

    // Store previous feedback for rollback on error
    const previousFeedback = messageFeedbackRef.current.get(messageId) ?? null;

    // Optimistic update: predict what the backend will do
    // If clicking same button, it will toggle off (remove)
    // If clicking different button, it will switch
    if (previousFeedback === feedbackType) {
      // Toggle off (remove) - set null to indicate explicit removal
      // This prevents fallback to embedded message feedback
      messageFeedbackRef.current.set(messageId, null);
    } else {
      // Create or update
      messageFeedbackRef.current.set(messageId, feedbackType);
    }
    setFeedbackVersion(v => v + 1);

    try {
      const accessToken = getAccessToken();
      const config = getApiConfig();
      const url = `${config.baseURL}/api/v1/chat/messages/${messageId}/feedback`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          conversationId: currentConversationId,
          feedbackType,
          ...(feedbackText ? { feedbackText } : {})
        }),
      });

      if (!response.ok) {
        throw new Error(`Feedback submission failed: ${response.status}`);
      }

      // Parse response to get the actual action performed
      const data = await response.json();
      const action = data.action as 'created' | 'updated' | 'removed';

      // Sync state with backend's actual result
      if (action === 'removed') {
        // Set null to indicate explicit removal (prevents fallback to embedded feedback)
        messageFeedbackRef.current.set(messageId, null);
        console.info(`🔄 Feedback removed for message ${messageId}`);
      } else if (action === 'created' || action === 'updated') {
        const actualFeedbackType = data.feedback?.feedbackType as FeedbackType;
        if (actualFeedbackType) {
          messageFeedbackRef.current.set(messageId, actualFeedbackType);
        }
        console.info(`✅ Feedback ${action}: ${actualFeedbackType} for message ${messageId}`);
      }
      setFeedbackVersion(v => v + 1);

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Rollback to previous state on error
      // previousFeedback can be 'positive', 'negative', or null (explicitly removed)
      messageFeedbackRef.current.set(messageId, previousFeedback);
      setFeedbackVersion(v => v + 1);
      // Don't show error to user - feedback is non-critical
    }
  }, [currentConversationId, getAccessToken]);

  /**
   * Regenerate a message (re-run AI on original query)
   * Marks the message as regenerating and triggers new streaming response
   * Properly restores original content on failure
   */
  const regenerateMessage = useCallback(async (messageId: string): Promise<void> => {
    if (!currentConversationId) {
      console.warn('Cannot regenerate - no active conversation');
      return;
    }

    // Don't allow regeneration while streaming
    if (isStreaming) {
      console.warn('Cannot regenerate while streaming');
      return;
    }

    // Find the bot message to regenerate in frontend state
    // IMPORTANT: Message must exist locally to regenerate - we need to show loading state
    const botMessage = messages.find(m => m.id === messageId && m.type === 'bot');

    // If message doesn't exist locally, we can't regenerate (can't show loading state)
    // This can happen after stream_resume if the message wasn't properly added
    if (!botMessage) {
      console.warn('Cannot regenerate - message not found in frontend state');
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'system',
        content: 'This response cannot be regenerated. Please ask your question again.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Save original content and timestamp for rollback on error
    const originalContent = botMessage.content;
    const originalTimestamp = botMessage.timestamp;
    const originalId = messageId; // Track original ID for cleanup

    try {
      // Mark the bot message as being regenerated (show loading state)
      // Clear content to show loading indicator
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, content: '', isStreaming: true }
          : msg
      ));

      // CRITICAL FIX: Set streamingMessageRef BEFORE calling API
      // This prevents duplicate messages when WebSocket chunks arrive before API response
      // Chunks will update this message instead of creating a new one
      streamingMessageRef.current = {
        id: messageId, // Track with ORIGINAL ID - will update when API returns new ID
        type: 'bot',
        content: '',
        timestamp: Date.now(),
        isStreaming: true
      };
      streamingBufferRef.current = '';

      // Set streaming state AFTER we've confirmed the message exists
      setStreamPhase('awaiting_first_chunk');
      setChatState('streaming');

      const accessToken = getAccessToken();
      const config = getApiConfig();
      const url = `${config.baseURL}/api/v1/chat/messages/${messageId}/regenerate`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          conversationId: currentConversationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Regenerate failed: ${response.status}`);
      }

      const data = await response.json();
      const newMessageId = data.data?.newMessageId;
      console.info(`🔄 Regenerating message ${messageId}, new ID: ${newMessageId}`);

      // If backend returns a new message ID, update the message ID in state
      // The actual content will come via WebSocket streaming
      // RACE CONDITION FIX: Check both original ID and current streamingMessageRef ID
      // because handleComplete or chunks might have already updated the message
      if (newMessageId) {
        const currentRefId = streamingMessageRef.current?.id;
        setMessages(prev => prev.map(msg => {
          // Match original ID OR the current streaming ref ID (if handleComplete already updated it)
          if (msg.id === originalId || (currentRefId && msg.id === currentRefId)) {
            return { ...msg, id: newMessageId };
          }
          return msg;
        }));

        // Update streamingMessageRef to track the new message ID
        // IMPORTANT: Keep existing ref properties, just update the ID
        if (streamingMessageRef.current) {
          streamingMessageRef.current.id = newMessageId;
        }
      }

    } catch (error) {
      console.error('Failed to regenerate message:', error);

      // CRITICAL: Restore the ORIGINAL message content and timestamp on error
      // Without this, the message is left empty with broken UI
      // Check both original ID and current ref ID (message might have been updated during streaming)
      const currentRefId = streamingMessageRef.current?.id;
      setMessages(prev => prev.map(msg => {
        if (msg.id === originalId || (currentRefId && msg.id === currentRefId)) {
          return { ...msg, id: originalId, content: originalContent, timestamp: originalTimestamp, isStreaming: false };
        }
        return msg;
      }));

      // Clean up streaming state
      streamingMessageRef.current = null;
      streamingBufferRef.current = '';

      setStreamPhase('idle');
      setChatState('idle'); // Set to idle, not error - original message is restored

      // Show error message to user so they know regeneration failed
      const errorText = error instanceof Error ? error.message : 'Failed to regenerate response';
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        type: 'system',
        content: `Could not regenerate response: ${errorText}. Please try again.`,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [currentConversationId, isStreaming, messages, getAccessToken]);

  /**
   * Check if a message can be regenerated
   * Returns true only if the message exists locally as a bot message
   * We need the message locally to show loading state during regeneration
   */
  const canRegenerateMessage = useCallback((messageId: string): boolean => {
    // Can't regenerate while streaming
    if (isStreaming) return false;

    // Find the message in current state - must exist locally
    const message = messages.find(m => m.id === messageId);
    if (!message) return false;

    // Must be a bot message
    if (message.type !== 'bot') return false;

    // Must have content (can't regenerate empty messages)
    if (!message.content || message.content.trim().length === 0) return false;

    return true;
  }, [messages, isStreaming]);

  /**
   * FIX #6: Save partial response on tab close/visibility change
   * Uses beforeunload and visibilitychange events to preserve streaming buffer
   * This ensures users don't lose AI responses if they accidentally close the tab
   */
  useEffect(() => {
    const savePartialResponse = (): void => {
      // Only save if we're streaming and have content in the buffer
      if (isStreaming && streamingBufferRef.current && streamingBufferRef.current.length > 0) {
        // Mark that we've saved a partial response
        partialResponseSavedRef.current = true;

        // Save the partial streaming content to localStorage for recovery
        // This can be recovered on next page load if conversation is resumed
        try {
          const partialData = {
            conversationId: currentConversationId,
            content: streamingBufferRef.current,
            timestamp: Date.now(),
            messageId: streamingMessageRef.current?.id || null
          };
          localStorage.setItem('amalq_chat_partial_response', JSON.stringify(partialData));
          console.info('💾 Saved partial streaming response for recovery');
        } catch (e) {
          console.error('Failed to save partial response:', e);
        }
      }

      // Also save draft message if user was typing
      if (draftMessageRef.current && draftMessageRef.current.length > 0) {
        try {
          const draftData = {
            conversationId: currentConversationId,
            content: draftMessageRef.current,
            timestamp: Date.now()
          };
          localStorage.setItem('amalq_chat_draft', JSON.stringify(draftData));
          console.info('💾 Saved draft message for recovery');
        } catch (e) {
          console.error('Failed to save draft:', e);
        }
      }
    };

    // Handle page unload (tab close, navigation away)
    const handleBeforeUnload = (): void => {
      savePartialResponse();
    };

    // Handle visibility change (tab becomes hidden)
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        savePartialResponse();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isStreaming, currentConversationId]);

  /**
   * DRAFT RECOVERY: Recover user's draft message on mount
   * NOTE: Stream resumption is now handled by backend via StreamBufferService
   * Backend sends 'stream_resume' event with buffered content when client reconnects
   */
  useEffect(() => {
    // Only attempt recovery once when chat opens
    if (widgetState !== 'open') return;

    try {
      // Check for saved draft (user's typed message that wasn't sent)
      const savedDraft = localStorage.getItem('amalq_chat_draft');
      if (savedDraft) {
        const draftData = JSON.parse(savedDraft) as {
          conversationId: string | null;
          content: string;
          timestamp: number;
        };

        // Only recover if it's recent (within last 30 minutes)
        const isRecent = Date.now() - draftData.timestamp < 30 * 60 * 1000;
        const matchesConversation = !currentConversationId || draftData.conversationId === currentConversationId;

        if (isRecent && matchesConversation && draftData.content) {
          console.info('📥 Recovered draft message');
          draftMessageRef.current = draftData.content;
        }

        // Clear the saved draft after recovery attempt
        localStorage.removeItem('amalq_chat_draft');
      }
    } catch (e) {
      console.error('Failed to recover draft:', e);
      localStorage.removeItem('amalq_chat_draft');
    }
  }, [widgetState, currentConversationId]);

  // Cleanup queue timeout and countdown interval on unmount
  useEffect((): (() => void) => {
    return (): void => {
      if (queueProcessTimeoutRef.current) {
        window.clearTimeout(queueProcessTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  // Save conversation when messages change
  useEffect(() => {
    if (messages.length > 0 && widgetState === 'closed') {
      saveCurrentConversation();
    }
  }, [messages, widgetState, saveCurrentConversation]);

  // Update conversation in ref when messages change (for streaming)
  useEffect(() => {
    if (conversationRef.current && messages.length > 0) {
      conversationRef.current = {
        ...conversationRef.current,
        messages,
        lastActivityAt: Date.now()
      };
    }
  }, [messages]);

  // Auto-load most recent conversation when history finishes loading and chat is open
  // CRITICAL: Use stable dependencies and prevent infinite loops with React best practices
  useEffect(() => {
    // Only auto-load if:
    // 1. Chat is open
    // 2. History has finished loading
    // 3. No current conversation is active
    // 4. History has conversations
    // 5. We haven't already attempted auto-load (prevent infinite loops)
    if (
      widgetState === 'open' && 
      !isLoadingHistory && 
      !currentConversationId && 
      historyConversations.length > 0 &&
      !autoLoadAttemptedRef.current
    ) {
      const recentConv = getMostRecentConversation();
      // Only auto-load if conversation hasn't failed before (404)
      if (recentConv && !failedConversationIdsRef.current.has(recentConv.id)) {
        // Mark as attempted to prevent infinite loops
        autoLoadAttemptedRef.current = true;

        // Auto-load the recent conversation
        loadConversation(recentConv.id).catch(() => {
          // Failed to auto-load recent conversation - create new one instead
          createNewConversation();
        });
      }
    }
    
    // Reset auto-load flag when widget closes or conversation changes
    if (widgetState === 'closed' || currentConversationId) {
      autoLoadAttemptedRef.current = false;
    }
  }, [widgetState, isLoadingHistory, currentConversationId, historyConversations.length, getMostRecentConversation, loadConversation, createNewConversation]);

  return {
    // State
    widgetState,
    chatState,
    messages,
    currentConversationId,
    hasUnreadMessages,
    authState,
    isStreaming,
    showTypingIndicator,
    rateLimit,
    queuedMessagesCount,
    rateLimitRetryAfter,
    isLoadingConversation,
    loadingConversationId,
    showRateLimitModal,
    rateLimitError,
    rateLimitType, // Which limit was hit: 'burst' or 'window'
    draftMessage: draftMessageRef.current, // FIX #3: Expose draft for input recovery

    // Actions
    openChat,
    closeChat,
    toggleHistory,
    sendMessage,
    startNewConversation,
    loadConversation,
    continueAsGuest,
    closeRateLimitModal,
    handleSuggestionClick,
    getSuggestionsForMessage,
    retryMessage, // FIX #5: Retry failed messages
    dismissSuggestions, // FIX #8: Dismiss suggestions
    setDraftMessage, // FIX #3: Update draft content
    // Message actions
    submitFeedback,
    regenerateMessage,
    canRegenerateMessage, // Check if regeneration is possible for a message
    messageFeedback: messageFeedbackRef.current, // Read-only access to feedback state
  };
}
